import { useState } from 'react'
import { Activity, RefreshCw, FileText, Database, Server, AlertTriangle } from 'lucide-react'
import { getAdminHealth, getAdminLogs } from '../../services/api'
import {
  C, Card, Row, Btn, Chips, BigStat, Loading, ErrorLine, Empty,
  useAsync, fmtDateTime, fmtLogLine, fmtSize, fmtUptime, Pill, divider, NETWORK_LABELS,
} from './adminUI'

// Раздел «Здоровье»: состояние сервера, мониторов сетей, воркеров, БД + хвост
// лога API. Главное, за чем тут следят: если монитор сети «молчит» больше
// 10 минут (stale), оплаты перестают подтверждаться автоматически — заявки
// будут висеть в pending, пока их не подтвердят вручную в разделе «Оплаты».

const TABLE_LABELS = {
  users: 'Пользователи',
  trades: 'Сделки',
  bots: 'Боты',
  payment_requests: 'Заявки на оплату',
  activity_log: 'Журнал событий',
  support_messages: 'Сообщения поддержки',
  utm_tracking: 'UTM-переходы',
}

export default function HealthSection() {
  const [only, setOnly] = useState('errors')
  const health = useAsync(() => getAdminHealth(), [])
  const logs = useAsync(() => getAdminLogs({ lines: 150, only: only === 'errors' ? 'errors' : undefined }), [only])

  const h = health.data
  const stale = (h?.monitors || []).filter(m => m.stale)
  const lines = logs.data?.lines || []

  return (
    <div>
      <ErrorLine onRetry={health.reload}>{health.error}</ErrorLine>

      {health.loading && !h ? <Loading /> : !h ? <Empty /> : (
        <>
          {!!stale.length && (
            <div style={{
              display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 12,
              background: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.25)',
              borderRadius: 12, padding: '10px 12px',
            }}>
              <AlertTriangle size={15} color={C.danger} style={{ flexShrink: 0, marginTop: 1 }} />
              <div style={{ fontSize: 11.5, color: C.danger, lineHeight: 1.5 }}>
                Мониторы молчат: {stale.map(m => m.network).join(', ')}. Автоподтверждение
                оплат в этих сетях не работает — проверьте процесс мониторинга.
              </div>
            </div>
          )}

          <Card
            title="Сервер API"
            icon={Server}
            right={<Btn small ghost icon={RefreshCw} color={C.muted} onClick={health.reload}>Обновить</Btn>}
          >
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <BigStat label="Работает" value={fmtUptime(h.server?.uptimeSec)} color={C.accent} />
              <BigStat label="Память" value={`${h.server?.memoryMb ?? '—'}МБ`} color={C.indigo} />
              <BigStat label="Порт" value={h.server?.port ?? '—'} color="#fff" />
            </div>
            <Row label="Запущен" value={fmtDateTime(h.server?.startedAt)} />
            <Row label="Node / платформа" value={`${h.server?.node || '—'} · ${h.server?.platform || '—'}`} mono />
            <Row label="PID" value={h.server?.pid ?? '—'} mono />
          </Card>

          <Card title="Мониторы сетей" icon={Activity} accent={stale.length ? C.danger : C.accent}>
            {!(h.monitors || []).length ? <Empty>Мониторы ещё не запускались</Empty> : h.monitors.map(m => (
              <Row
                key={m.network}
                label={NETWORK_LABELS[m.network] || m.network}
                value={m.ageSec == null ? 'нет данных' : m.ageSec < 90 ? 'в норме' : `${Math.round(m.ageSec / 60)} мин назад`}
                valueColor={m.stale ? C.danger : C.accent}
                valueSub={`блок ${m.last_block ?? '—'} · ${fmtDateTime(m.updated_at)}`}
              />
            ))}
          </Card>

          <Card title="Боты и воркеры" icon={Activity} accent={C.indigo}>
            <Row label="Ботов всего" value={h.bots?.total ?? 0} />
            <Row label="Работают" value={h.bots?.running ?? 0} valueColor={C.accent} />
            <Row label="С записанным pid" value={h.bots?.withPid ?? 0} />
            <Row
              label="Зомби (pid мёртв)"
              value={h.bots?.zombies ?? '—'}
              valueColor={h.bots?.zombies ? C.warn : C.dim}
            />
            <div style={divider} />
            {Object.keys(h.workersBy || {}).length === 0 ? (
              <div style={{ fontSize: 11.5, color: C.dim }}>Слоты воркеров пусты</div>
            ) : Object.entries(h.workersBy).map(([k, v]) => (
              <Row key={k} label={`Воркеры: ${k}`} value={v} />
            ))}
            {!!(h.activeWorkers || []).length && (
              <>
                <div style={divider} />
                <div style={{ fontSize: 11, color: C.dim, marginBottom: 6 }}>Активные слоты</div>
                {h.activeWorkers.slice(0, 10).map(w => (
                  <Row
                    key={`${w.user_id}:${w.bot_id}:${w.worker_id}`}
                    label={`${w.symbol || '—'} (юзер ${w.user_id})`}
                    value={w.status}
                    valueSub={`цель ${fmtDateTime(w.target_time)}`}
                  />
                ))}
              </>
            )}
          </Card>

          <Card title="База данных" icon={Database} accent={C.muted}>
            <Row label="Размер файла" value={fmtSize(h.db?.size)} />
            <Row label="Путь" value={h.db?.path || '—'} mono />
            <div style={divider} />
            {Object.entries(h.db?.tables || {}).map(([k, v]) => (
              <Row key={k} label={TABLE_LABELS[k] || k} value={v == null ? '—' : v} />
            ))}
            <div style={divider} />
            <div style={{ fontSize: 11, color: C.dim, marginBottom: 6 }}>Свободные адреса</div>
            {!(h.freeAddresses || []).length ? (
              <div style={{ fontSize: 11.5, color: C.danger }}>Свободных адресов нет ни в одной сети</div>
            ) : h.freeAddresses.map(a => (
              <Row
                key={a.network}
                label={NETWORK_LABELS[a.network] || a.network}
                value={a.c}
                valueColor={a.c === 0 ? C.danger : a.c < 3 ? C.warn : C.accent}
              />
            ))}
          </Card>
        </>
      )}

      <Card
        title="Лог API"
        icon={FileText}
        accent={C.warn}
        right={<Btn small ghost icon={RefreshCw} color={C.muted} onClick={logs.reload}>Обновить</Btn>}
      >
        <Chips
          options={[{ key: 'errors', label: 'Только ошибки' }, { key: 'all', label: 'Все строки' }]}
          value={only}
          onChange={setOnly}
        />
        <ErrorLine onRetry={logs.reload}>{logs.error}</ErrorLine>
        {logs.data && !logs.data.exists && <Empty>Файл лога ещё не создан</Empty>}
        {logs.loading && !lines.length ? <Loading /> : !lines.length ? (
          <Empty>{only === 'errors' ? 'Ошибок в логе нет' : 'Лог пуст'}</Empty>
        ) : (
          <div style={{
            background: '#0A0A0A', border: `1px solid ${C.line}`, borderRadius: 10,
            padding: '8px 10px', maxHeight: 320, overflowY: 'auto', overflowX: 'auto',
          }}>
            {/* appendLog пишет метку в UTC (toISOString) — переводим в пояс
                админки через fmtLogLine, иначе время расходится на 3 часа
                с остальной панелью (замечание владельца 2026-09-23). */}
            {lines.slice().reverse().map((l, i) => (
              <div key={i} style={{
                fontSize: 10.5, lineHeight: 1.55, fontFamily: 'monospace',
                color: /ошиб|error|failed|❌|exception|traceback/i.test(l) ? C.danger
                  : /⚠️|warn/i.test(l) ? C.warn : C.muted,
                whiteSpace: 'pre-wrap', overflowWrap: 'anywhere',
                borderTop: i ? `1px solid rgba(255,255,255,0.03)` : 'none',
                padding: '3px 0',
              }}>{fmtLogLine(l)}</div>
            ))}
          </div>
        )}
        {logs.data?.size != null && (
          <div style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
            <Pill color={C.muted}>{fmtSize(logs.data.size)}</Pill>
            <div style={{ fontSize: 10.5, color: C.dim, flex: 1, overflowWrap: 'anywhere' }}>{logs.data.file}</div>
          </div>
        )}
        <div style={{ fontSize: 10.5, color: C.dim, marginTop: 8, lineHeight: 1.45 }}>
          Новые строки сверху. Читается хвост файла (последние 256 КБ), максимум 150 строк.
        </div>
      </Card>
    </div>
  )
}
